import React, { useCallback, useMemo } from 'react';
import { Container, ExpandableSection, SpaceBetween } from '@amzn/awsui-components-core';
import { FolderNodeState } from '../../../hooks/useWorkDocsFolderBrowser';
import { FileUploadState } from '../../../hooks/useWorkDocsUpload';
import { useDeleteConfirmation } from '../../../hooks/useDeleteConfirmation';
import { WorkDocsFolderTree } from '../WorkDocsFolderTree';
import { FolderUploadModal } from '../FolderUploadModal';
import { DeleteDocumentModal } from '../DeleteDocumentModal';
import { SupplementalContextGuide } from '../SupplementalContextGuide';
import { ManagerHubRatingsGuide } from '../ManagerHubRatingsGuide';
import { UPLOAD_MAX_DOCS_PER_FOLDER, UPLOAD_MAX_MANAGER_HUB_DOCS } from '../../../constants/BaseballCardConstants';
import { MANAGER_HUB_EXPORTS_FOLDER } from '../../../utils/workDocsFolderPlan';
import { resolveFolderId } from '../../../utils/folderLinks';

export interface Step3ReviewAndUploadProps {
  rootFolderId: string | null;
  rootFolderName: string;
  expandedIds: Set<string>;
  nodeStateById: Map<string, FolderNodeState>;
  onExpandFolder: (folderId: string) => void;
  onCollapseFolder: (folderId: string) => void;
  isUploading: boolean;
  /** Per-folder upload status: outer key = folder id, inner = file name. */
  statusByFolder: Map<string, Map<string, FileUploadState>>;
  onUpload: (folderId: string, files: File[]) => void;
  /**
   * Deletes one document (by id) from its parent folder and refreshes the listing; resolves
   * `true` on success, `false` on failure. Side-effect lives in useWorkDocsDelete on the page.
   */
  onDeleteFile: (parentFolderId: string, documentId: string) => Promise<boolean>;
  /**
   * Which folder's upload modal is open (null = closed). Lifted to the page so the Step 3
   * how-to guides can open the same modal by folder id, not just the tree's Upload button.
   */
  uploadFolderId: string | null;
  /** Opens/closes the upload modal for a folder id (or null to close). */
  onSetUploadFolderId: (folderId: string | null) => void;
  /**
   * Logins selected in Step 2. Passed to the tree so the "Supplemental Context" subtree only
   * shows folders for selected people (see WorkDocsFolderTree).
   */
  selectedAliases: ReadonlySet<string>;
  /** Signed-in user's alias, for the Manager Hub guide's per-user "Manager Hub" run link. */
  alias: string;
}

/**
 * Resolves a folder's display name from the browser cache: the root uses `rootFolderName`;
 * any other folder is looked up by scanning parents' cached child listings for a match.
 * Falls back to a generic label so the modal header always has something to show.
 */
const resolveFolderName = (
  folderId: string,
  rootFolderId: string | null,
  rootFolderName: string,
  nodeStateById: Map<string, FolderNodeState>
): string => {
  if (folderId === rootFolderId) return rootFolderName;
  for (const state of nodeStateById.values()) {
    const match = state.folders.find((folder) => folder.id === folderId);
    if (match) return match.name;
  }
  return 'Folder';
};

/**
 * Step 3: the provisioned WorkDocs folders render first as a tree with per-folder upload, followed
 * by two collapsible how-to guides — Manager Hub Exports (expanded by default) then Supplemental
 * Context (collapsed). Each folder's Upload action — and the Manager Hub guide's folder link —
 * opens a modal scoped to that folder; upload status is shown only for the folder being uploaded to.
 */
export const Step3ReviewAndUpload: React.FC<Step3ReviewAndUploadProps> = ({
  rootFolderId,
  rootFolderName,
  expandedIds,
  nodeStateById,
  onExpandFolder,
  onCollapseFolder,
  isUploading,
  statusByFolder,
  onUpload,
  onDeleteFile,
  uploadFolderId,
  onSetUploadFolderId,
  selectedAliases,
  alias
}) => {
  const uploadFolderName = useMemo(
    () => (uploadFolderId ? resolveFolderName(uploadFolderId, rootFolderId, rootFolderName, nodeStateById) : ''),
    [uploadFolderId, rootFolderId, rootFolderName, nodeStateById]
  );

  // Opens the upload modal for a fixed subfolder identified by NAME (as the how-to guides do).
  // Resolves the name to its id from the root's lazily-loaded child listing; no-ops (returns
  // undefined below) if not resolvable yet — the guide then renders the folder as plain text.
  const openUploadForFolderName = useCallback(
    (folderName: string) => {
      const rootChildFolders = rootFolderId ? nodeStateById.get(rootFolderId)?.folders ?? [] : [];
      const folderId = resolveFolderId(folderName, rootFolderId, rootChildFolders);
      if (folderId) onSetUploadFolderId(folderId);
    },
    [rootFolderId, nodeStateById, onSetUploadFolderId]
  );

  // Whether the Manager Hub Exports folder id is resolvable yet (root provisioned + listing loaded).
  // Gates the guide's inline "upload" affordance: an opener is passed only when the target exists.
  const managerHubUploadReady = useMemo(
    () => !!resolveFolderId(MANAGER_HUB_EXPORTS_FOLDER, rootFolderId, rootFolderId ? nodeStateById.get(rootFolderId)?.folders ?? [] : []),
    [rootFolderId, nodeStateById]
  );

  // Manager Hub Exports holds exactly one team-wide ratings export, so cap that folder at a single
  // file; every other folder uses the standard per-folder limit. Keyed off the resolved folder
  // NAME (not id) since it's the stable identifier the folder plan exposes.
  const uploadMaxDocs = uploadFolderName === MANAGER_HUB_EXPORTS_FOLDER ? UPLOAD_MAX_MANAGER_HUB_DOCS : UPLOAD_MAX_DOCS_PER_FOLDER;

  // Delete is destructive, so the tree's delete action opens a confirmation first; the modal owns
  // the confirm lifecycle (in-flight + inline error) and only runs the real delete on confirm. The
  // pending target is local UI state, not lifted to the page — nothing outside Step 3 deletes.
  const {
    pendingDelete,
    isDeleting,
    error: deleteError,
    requestDelete,
    confirmDelete,
    cancel
  } = useDeleteConfirmation({
    deleteFile: onDeleteFile
  });

  return (
    <Container>
      <SpaceBetween size="m">
        <WorkDocsFolderTree
          rootFolderId={rootFolderId}
          rootFolderName={rootFolderName}
          expandedIds={expandedIds}
          nodeStateById={nodeStateById}
          onExpandFolder={onExpandFolder}
          onCollapseFolder={onCollapseFolder}
          onUploadToFolder={onSetUploadFolderId}
          selectedAliases={selectedAliases}
          onDeleteFile={(parentFolderId, documentId) => {
            // Resolve the file name from the folder's cached documents so the confirmation can
            // name it; fall back to a generic label if the listing isn't cached for some reason.
            const fileName = nodeStateById.get(parentFolderId)?.documents.find((doc) => doc.id === documentId)?.name ?? 'this document';
            requestDelete({ parentFolderId, documentId, fileName });
          }}
        />

        {/* How-to guides sit below the folder tree. Manager Hub Exports first and expanded by
            default (the primary source teams upload), then Supplemental Context collapsed. The
            Manager Hub guide's folder link opens the in-page upload modal once its folder id is
            resolvable (root provisioned + listing loaded); until then it renders as plain text. */}
        <ExpandableSection variant="footer" defaultExpanded headerText="How to Export from Manager Hub">
          <ManagerHubRatingsGuide
            alias={alias}
            onOpenUpload={managerHubUploadReady ? () => openUploadForFolderName(MANAGER_HUB_EXPORTS_FOLDER) : undefined}
          />
        </ExpandableSection>

        <ExpandableSection variant="footer" headerText="How to Add Supplemental Context">
          <SupplementalContextGuide />
        </ExpandableSection>

        {uploadFolderId && (
          <FolderUploadModal
            visible
            folderId={uploadFolderId}
            folderName={uploadFolderName}
            isUploading={isUploading}
            existingFileCount={nodeStateById.get(uploadFolderId)?.documents.length ?? 0}
            maxDocs={uploadMaxDocs}
            statusByFile={statusByFolder.get(uploadFolderId)}
            onUpload={onUpload}
            onDismiss={() => onSetUploadFolderId(null)}
          />
        )}

        {pendingDelete && (
          <DeleteDocumentModal
            visible
            fileName={pendingDelete.fileName}
            isDeleting={isDeleting}
            error={deleteError}
            onConfirm={confirmDelete}
            onDismiss={cancel}
          />
        )}
      </SpaceBetween>
    </Container>
  );
};
